/**
 * hooks/useMyOeaToday.js — Нэвтэрсэн ажилтны ӨНӨӨДРИЙН ӨЭА/наряд бүртгэл.
 *
 * ЯАГААД ХЭРЭГТЭЙ ВЭ:
 *   `/me` хуудсанд WorkerHero ба PreworkBanner ХОЁУЛАА `getMyOeaRegistration`
 *   -ыг дуудаж, ижил өгөгдлийг ХОЁР УДАА татдаг байв. Хуудас ачаалах бүрд
 *   илүүц нэг сүлжээний дуудлага явж, хоёр компонент өөр өөр хугацаанд
 *   шинэчлэгдэж «анивчдаг» байсан.
 *
 * ЗАРЧИМ:
 *   Модулийн түвшний promise кэш — ижил (ТН + огноо)-той зэрэг дуудлагууд
 *   НЭГ хүсэлт хуваалцана. TTL дуустал давтан татахгүй.
 *
 *   Кэш нь МОДУЛИЙН хамрах хүрээнд (component биш) тул хоёр өөр компонент
 *   ижил өгөгдлийг хүсэхэд нэг л хүсэлт явна.
 */
import { useCallback, useEffect, useState } from "react";
import { getMyOeaRegistration } from "src/api/hazard";

// Ижил өгөгдлийг дахин татахгүй байх хугацаа. Ажлын бүртгэл нэг өдөрт
// ховор өөрчлөгддөг тул 60 секунд хангалттай.
const TTL_MS = 60 * 1000;

// { "tn|date": { at: number, promise: Promise } }
const cache = new Map();

export const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
};

/**
 * Кэштэй татах. `force` бол кэшийг тойрч шинээр татна.
 * @returns {Promise<object|null>}
 */
export function fetchMyOea(tn, workDate, force = false) {
  if (!tn) return Promise.resolve(null);
  const key = `${tn}|${workDate || ""}`;
  const hit = cache.get(key);
  if (!force && hit && Date.now() - hit.at < TTL_MS) return hit.promise;

  const params = { tn };
  if (workDate) params.work_date = workDate;
  const promise = getMyOeaRegistration(params).catch(() => null);
  cache.set(key, { at: Date.now(), promise });
  return promise;
}

/** Кэшийг цэвэрлэх — бүртгэл өөрчлөгдсөний дараа (ж: АШУА бүртгэл нэмсэн). */
export function invalidateMyOea() {
  cache.clear();
}

/**
 * useMyOeaToday — компонентод зориулсан бүрхүүл.
 *
 * @param {string} tn        ажилтны ТН
 * @param {object} options
 * @param {boolean} options.today  өнөөдрийн огноогоор шүүх эсэх
 * @param {boolean} options.skip   огт татахгүй (шаардлагагүй үед)
 */
export function useMyOeaToday(tn, { today = false, skip = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!skip && !!tn);

  const workDate = today ? todayStr() : undefined;

  const refresh = useCallback(
    (force = true) => {
      if (!tn || skip) {
        setLoading(false);
        return Promise.resolve(null);
      }
      setLoading(true);
      return fetchMyOea(tn, workDate, force)
        .then((r) => {
          setData(r || null);
          return r;
        })
        .finally(() => setLoading(false));
    },
    [tn, workDate, skip],
  );

  useEffect(() => {
    let alive = true;
    if (!tn || skip) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    fetchMyOea(tn, workDate, false)
      .then((r) => alive && setData(r || null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [tn, workDate, skip]);

  return { data, loading, refresh };
}
